import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Modal } from '../../Modal';
import { Avatar, PageLoader, ScrollArea, Typography } from '../ui';
import { ChannelChip } from './ChannelChip';
import { ContactReadError } from './ContactReadError';
import { OutreachClose } from '../tasks/OutreachClose';
import { StageCard } from './StageCard';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { refreshContactData, useContactEntryQuery, type ContactId, type WebOutreachMessage } from '../../../lib/contact-book';
import { composerVisible, mergeTranscript, type WireOwnerEvent } from '../../../lib/outreach-lane';
import { ownerNoteActionVisible } from '../../../lib/owner-note-refresh';
import { OutreachThreadTranscript, OwnerOnlyCaption } from '../outreach/OutreachThreadTranscript';
import { TellAgentComposer } from '../outreach/TellAgentComposer';
import { contactDisplayName, threadChannel } from '../../../lib/contact-rows';
import { stageCard, threadStage, uniqueThreads, type OutreachStage } from '../../../lib/outreach-stage';
import { threadStartedBy, type ThreadViewContext } from '../../../lib/outreach-thread-view';
import { renderStartedBy } from '../../../lib/outreach-thread-view-render';
import type { WebOutreachThread } from '../../../lib/contact-book';
import { useAgentName } from '../../../lib/use-agent-name';
import { useNarrow } from '../../../lib/use-narrow';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import styles from './contacts.module.css';

type TFn = (key: string, opts?: Record<string, unknown>) => string;
type LogClick = (params: { page_name: string; button_name: string }) => void;

/**
 * The outreach conversation viewer — ONE OUTREACH, in a dialog (a sheet on narrow), opened from a
 * contact row whose contact has a single conversation, from a conversation row on the contact screen,
 * or from an Inbox row.
 *
 * It used to be keyed on a CHANNEL and stitched every outreach on that channel into one scroll. That
 * read as a single conversation while being several: a reply landed on whichever of them was still
 * open, a closed sibling could disable the composer on a live thread, and the older ones were
 * unreachable from any list. A conversation is an outreach; a channel is only how it was delivered.
 *
 * It renders the correspondence as a chat through the shared `OutreachThreadTranscript` (the speaker model in
 * `lib/outreach-thread-view.ts`: my side on the end edge, theirs on the start edge, a person's words in a solid
 * bubble and an agent's in a tinted one, each run labelled once, day dividers and clock times), with the owner's own lane — what they told their agent, chose, approved, answered
 * or closed — between the messages (`mergeTranscript`, `lib/outreach-lane.ts`), and answers it with the
 * "Tell {agent}" composer. The composer never puts text on the channel: the owner's words go to their
 * own agent, which sends them on this thread or acts on them. The sheet stays open on send.
 *
 * Emoji reactions are deliberately absent: an outreach message has no id and no reaction backend to
 * persist one to, so a react would vanish on the next read. Copy — which needs no backend — is kept, as a hover action. The
 * quote-Reply went with the old composer: a quote has nowhere to ride on a note to the agent.
 */
export function ContactOutreachViewer({
  personId,
  recipientId,
  outreachId,
  open,
  onClose,
  onOpenContact,
}: {
  personId: string;
  /** Set (with an empty `personId`) for a contact the people book knows only by an address — the
   *  conversation is then read by recipient id, exactly as the recipient-keyed Contacts detail is. */
  recipientId?: string;
  /** THE ONE OUTREACH to open. It used to be a channel key, and the viewer stitched every outreach on
   *  that channel into a single scroll — which read as one conversation while being several, so a
   *  reply landed on whichever of them happened to still be open and the others were unreachable.
   *  A conversation is an outreach; the channel is only how it was delivered. */
  outreachId: string;
  open: boolean;
  onClose: () => void;
  /** Present when opened from the contacts LIST — pressing the header navigates to the contact screen.
   *  Absent when opened FROM the contact screen (pressing the header does nothing). */
  onOpenContact?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { logPageClickBi, logModalActionBi } = useGenericBi();
  const narrow = useNarrow();
  // Person or address-only identity — the read is keyed on whichever the row carried.
  const contactId: ContactId = personId ? { personId } : { recipientId: recipientId ?? '' };
  const { entry, isLoading, isError, refetch } = useContactEntryQuery(open ? contactId : { personId: '' });
  const agent = useAgentName();

  // A contact the people book knows only by an address (recipientId, no personId) is the #10660 path.
  const contactKind = personId ? 'person' : 'recipient';
  // Opened FROM the contacts/inbox LIST (the header can navigate to the record) reports `contacts`;
  // opened from the contact detail card (no navigation target) reports `contact_detail`.
  const page = onOpenContact ? 'contacts' : 'contact_detail';

  // The dialog reports itself opening, once per opening — the modal event the trigger row's click
  // (open_contact / open_inbox_row / needs_you_channel) cannot stand in for.
  useEffect(() => {
    if (!open) return;
    logModalActionBi(
      { page_name: page, modal_name: 'open_conversation_viewer', action: 'open' },
      { additionalFields: { contact_kind: contactKind } },
    );
    // Keyed on `open`: a re-read or a step inside the dialog must not re-report it as a new opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The contact read fetches on open (whole threads, not the list preview) — track it as a load region.
  // Drive it off the query's REAL state, not `!entry`: on a fetch error `entry` stays undefined, so
  // `!entry` would sit "loading" forever and loadFinish would never fire. `isLoading` settles on both
  // success and failure, and `isError` carries the outcome into `isSuccessful`.
  useLoadBi(
    'contact-outreach-viewer',
    { isLoading, isError },
    { enabled: open, additionalFields: { contact_kind: contactKind } },
  );

  if (!open) return null;

  // THIS outreach, and only it. Its own messages, in the order the service recorded them.
  // Through `uniqueThreads`, so a self-outreach resolves to its outbound copy and the header knows which
  // side started it.
  const found = entry ? uniqueThreads(entry).find((candidate) => candidate.thread.outreachId === outreachId) : undefined;
  const thread = found?.thread;
  // Through the shared rule, NOT the raw field. symphony-bff now names the in-app channel (`app`) on the wire;
  // the rule's empty → `app` normalisation stays as a defensive fallback for rows that predate that, since
  // `ChannelChip` draws nothing for an empty value and the header would silently lose its badge.
  const channel = thread ? threadChannel(thread) : '';
  // In WIRE order, never re-sorted: the server's order is the transcript's, and the owner's lane is merged
  // into it by time (`mergeTranscript`), the same on all three surfaces.
  const messages = thread?.messages ?? [];
  const stage = thread ? threadStage(thread) : undefined;
  // Same fallback the list row and detail header use, so the viewer's header, initials and "them"
  // attribution name an address-only / nameless contact by its address rather than a blank line.
  const theirName = contactDisplayName(entry?.contact, entry?.channels, t('contacts.row.unnamed'));
  const direction = found?.direction ?? 'outbound';

  return (
    <Modal
      open
      onClose={onClose}
      size="small"
      maxHeightPx={650}
      contentClassName={styles.viewerContentSlot}
      presentation={narrow ? 'sheet' : 'centered'}
      labelledBy="outreach-viewer-title"
    >
      <ViewerContent
        personId={personId}
        channel={channel}
        threadOutreachId={outreachId}
        thread={thread}
        stage={stage}
        onSettled={onClose}
        contactId={contactId}
        messages={messages}
        speakerCtx={{ contactName: theirName, direction, channel: thread?.channel }}
        startedBy={
          thread
            ? renderStartedBy(threadStartedBy(thread, direction, theirName, new Date()), t as unknown as TFn, agent.name, i18n.language || undefined)
            : ''
        }
        maestroAgentName={agent.name}
        refetch={refetch}
        loading={isLoading}
        failed={!isLoading && !entry}
        canRetry={isError}
        onRetry={refetch}
        theirName={theirName}
        theirPhoto={entry?.contact?.effectiveAvatarUrl}
        logClick={logPageClickBi as unknown as LogClick}
        onOpenContact={onOpenContact}
      />
    </Modal>
  );
}

function ViewerContent({
  personId,
  channel,
  threadOutreachId,
  thread,
  stage,
  onSettled,
  contactId,
  messages,
  speakerCtx,
  maestroAgentName,
  startedBy,
  refetch,
  loading,
  failed,
  canRetry,
  onRetry,
  theirName,
  theirPhoto,
  logClick,
  onOpenContact,
}: {
  personId: string;
  channel: string;
  /** The conversation itself. */
  threadOutreachId: string;
  /** The thread, for its pinned stage card. Undefined while the read is in flight. */
  thread?: WebOutreachThread;
  /** Its stage: Close is offered only while it is not Done. */
  stage?: OutreachStage;
  onSettled: () => void;
  contactId: ContactId;
  messages: WebOutreachMessage[];
  /** Who each message is from — the speaker model's context (the contact, which side started it, the channel). */
  speakerCtx: ThreadViewContext;
  maestroAgentName: string;
  /** "{agent} started this for you · Sep 24" / "{name}'s agent started this · Jul 19". Empty while the thread is not in hand. */
  startedBy: string;
  /** Re-runs the person-scoped read — the composer's refetch, and the one that carries the lane. */
  refetch: () => void;
  loading: boolean;
  /**
   * The read SETTLED with no contact to show — keyed on the ENTRY, not on the message count.
   *
   * Message count was the wrong predicate in both directions. A query that settles successfully with
   * `contacts: []` (a stale or deleted list row) leaves `isError` false, so a zero-message test let the
   * sheet render an empty transcript over a live composer — a reply against a contact that never
   * loaded. And a genuinely EMPTY conversation whose refresh failed has zero messages too, so it was
   * misclassified as a failure and shown an error instead of its real empty self.
   */
  failed: boolean;
  /** Whether retrying can help: a real error, as opposed to a read that succeeded and carried nothing. */
  canRetry: boolean;
  onRetry: () => void;
  theirName: string;
  theirPhoto?: string;
  logClick: LogClick;
  onOpenContact?: () => void;
}) {
  // The composer's own TOLD/PENDING items, until the server's event with the same noteId replaces them.
  // Held here, so they go when the sheet does.
  const [optimistic, setOptimistic] = useState<WireOwnerEvent[]>([]);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const transcript = mergeTranscript(messages, thread?.ownerEvents, optimistic);

  // Open at the newest message — a correspondence is read at its end.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [loading, transcript.length]);

  const headerPressable = Boolean(onOpenContact);
  const openContact = () => {
    if (!onOpenContact) return;
    logClick({ page_name: 'contact_thread', button_name: 'open_contact_from_viewer' });
    onOpenContact();
  };

  return (
    <div className={styles.viewer}>
      <header className={styles.viewerHeader}>
        <button type="button" className={headerPressable ? styles.viewerIdentityPressable : styles.viewerIdentity} onClick={openContact} disabled={!headerPressable}>
          <Avatar initials={avatarInitials(theirName)} photoUrl={theirPhoto} size={40} color={avatarSlotFor(personId || theirName)} />
          <span className={styles.viewerIdentityText}>
            <Typography variant="h3" id="outreach-viewer-title" className={styles.viewerName}>
              {theirName}
            </Typography>
            <ChannelChip channel={channel} />
            {startedBy ? (
              <Typography variant="p3" color="secondary">
                {startedBy}
              </Typography>
            ) : null}
          </span>
        </button>
      </header>

      <ScrollArea className={styles.viewerThread}>
        {loading ? (
          <PageLoader />
        ) : failed ? (
          <ContactReadError canRetry={canRetry} onRetry={onRetry} />
        ) : (
          <>
            {/* The stage card, pinned above the correspondence: the open question (answered in place),
                the scheduled / stuck line, or how it ended. A handling thread pins nothing. */}
            {thread ? (
              <>
                <StageCard thread={thread} contactName={theirName} page="contact_thread" />
                {/* The card is the owner's own view of the thread — the contact never sees it. */}
                {stageCard(thread) ? <OwnerOnlyCaption /> : null}
              </>
            ) : null}
            <OutreachThreadTranscript
              messages={messages}
              transcript={transcript}
              ctx={speakerCtx}
              maestroAgentName={maestroAgentName}
              outreachId={threadOutreachId}
              page="contact_thread"
              contactPhoto={theirPhoto}
              avatarKey={personId || theirName}
            />
            <div ref={bottomRef} />
          </>
        )}
      </ScrollArea>

      {/* Close — the owner finishing the conversation. Sits just above the composer and right-aligned, so
          the way OUT of a thread shares an edge with the way to answer it. Hidden once the thread is Done
          (`OutreachClose` returns nothing then). The same component the task modal and the full-page
          thread use, so the write, the failure line and the BI stay in one place. Non-optimistic: write,
          re-read, then dismiss. */}
      {stage && !failed ? (
        <OutreachClose
          outreachId={threadOutreachId}
          stage={stage}
          startIcon={<Check size={16} aria-hidden />}
          align="end"
          pageName="contact_thread"
          refresh={() => refreshContactData(contactId)}
          onClosed={onSettled}
        />
      ) : null}

      {/* No composer over a read that FAILED: the thread we would be telling the agent about was never
          loaded, so we could not confirm it — and the empty transcript above it reads as "they said
          nothing", which is a different thing from "we could not look". Shown on DONE threads too:
          telling the agent can reopen or follow up (`composerVisible`). Hidden while the read is in flight. */}
      {failed ? null : (
        // Only once the thread is IN HAND — never over the loader — like both phones.
        !loading && composerVisible(thread, false) ? (
        <TellAgentComposer
          scope={{ kind: 'thread', outreachId: threadOutreachId, contactName: theirName, stage: thread?.stage }}
          page="contact_thread"
          inSheet
          refetch={refetch}
          isSettled={(noteId) => ownerNoteActionVisible(thread, noteId)}
          onSent={(event) => setOptimistic((prev) => [...prev, event])}
        />
        ) : null
      )}
    </div>
  );
}
